import type { MediaMode, ResolutionChoice } from "./types"

/**
 * Build-time configuration. Values prefixed with VITE_ are read from
 * environment variables / .env files at build time and inlined into the
 * static bundle.
 *
 * SECURITY NOTE: This file is shipped to every browser. Do NOT add anything
 * here that would be a secret in any other environment. clip2commons is a
 * pure-SPA OAuth public client and uses PKCE (RFC 7636) - the client secret
 * is intentionally absent.
 */

export const OAUTH_CLIENT_ID = (import.meta.env.VITE_OAUTH_CLIENT_ID || "") as string
export const OAUTH_REDIRECT_URI = (import.meta.env.VITE_OAUTH_REDIRECT_URI || "") as string
/**
 * Client secret - only needed when the OAuth consumer was registered as
 * "confidential client: Yes" on Wikimedia. For truly public/native clients
 * (confidential: No) this must be left empty and PKCE alone is used.
 * NOTE: This value is baked into the JS bundle and visible to anyone who
 * reads the source. Wikimedia accepts this for WMCS-hosted tools because
 * the secret is considered semi-public for browser clients, and PKCE still
 * prevents code interception attacks.
 */
export const OAUTH_CLIENT_SECRET = (import.meta.env.VITE_OAUTH_CLIENT_SECRET || "") as string
/** Optional escape hatch for local dev only. Must NEVER be set in production. */
export const LEGACY_ACCESS_TOKEN = (import.meta.env.VITE_OAUTH_ACCESS_TOKEN || "") as string

export const OAUTH_AUTHORIZE_URL = "https://meta.wikimedia.org/w/rest.php/oauth2/authorize"
export const OAUTH_TOKEN_URL = "https://meta.wikimedia.org/w/rest.php/oauth2/access_token"
export const OAUTH_PROFILE_URL = "https://meta.wikimedia.org/w/rest.php/oauth2/resource/profile"

export const COMMONS_API_URL = "https://commons.wikimedia.org/w/api.php"
export const UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024

/**
 * File size policy.
 *
 *   ≤ MAX_FILE_SIZE_BYTES (1 GB)          - silent accept
 *   ≤ HARD_MAX_FILE_SIZE_BYTES (2 GB)     - accept, after telling the user
 *                                           it may stutter while encoding
 *   >  HARD_MAX_FILE_SIZE_BYTES           - hard reject
 *
 * Why these numbers, and why they moved up from 500 MB / 1 GB:
 *
 * The old ceiling measured the wrong thing. On the re-encode path - which is
 * nearly every upload - the source is read through mediabunny's BlobSource,
 * which pulls ranges on demand and never holds the file. What actually costs
 * memory is the OUTPUT, which BufferTarget accumulates into one contiguous
 * ArrayBuffer. A 1.8 GB phone recording becomes roughly a 1 GB WebM, so the
 * peak is about 1 GB on a machine that was being told 1.8 GB was too big to
 * attempt.
 *
 * Passthrough was the one path whose cost really did scale with the source,
 * because it called file.arrayBuffer(). It now hands the File straight to
 * the uploader, which slices it lazily, so an untouched 2 GB WebM uploads on
 * a 5 MB working set.
 *
 * Commons itself accepts 5 GB (siteinfo maxuploadsize = 5368709120), so
 * these are our limits, not theirs. 2 GB is where a single contiguous
 * output allocation stops being something a browser will reliably grant.
 *
 * On low-memory devices, FilePicker further caps via the maxFileSize prop
 * (set from capability.effectiveMaxBytes) - so a phone under 4 GiB still
 * gets the 200 MB cap regardless of the ceilings here.
 */
/**
 * How many videos may sit in one queue.
 *
 * This is the number Commons itself uses. UploadWizard.config.php sets
 *   'maxUploads' => ...isAllowed( 'mass-upload' ) ? 500 : 50
 * so 50 is what an ordinary contributor gets there, and matching it means a
 * batch that works here works there. The 500 tier is not mirrored: reading
 * it needs an authenticated rights lookup, and 500 browser-side encodes in
 * one tab is not a thing to encourage.
 */
export const MAX_QUEUE_FILES = 50

/**
 * The ceiling on a device we have reason to think is memory-constrained:
 * under 4 GiB of reported RAM, or iOS, which reports nothing at all.
 *
 * Two numbers, because the thing that used to make big files dangerous is
 * gone on most devices. The output no longer lives in the JS heap: it
 * streams to the origin private file system, and the source was always read
 * in ranges rather than held. Measured peak heap on a 5 GB source was about
 * 30 MB, flat, against a 3.8 GB limit. What remains in memory is the codec
 * working set, which scales with RESOLUTION and not with file size.
 *
 * So the cap is set by whether that streaming path is actually available:
 *
 *   STREAMING  - the output goes to disk, so a large file costs no more
 *                heap than a small one. 1 GB, which is five times the old
 *                cap and lets a phone upload what a phone records.
 *   BUFFERED   - no OPFS, so the output accumulates in memory and the old
 *                reasoning still holds. Unchanged at 200 MB.
 *
 * The buffered figure stays deliberately low because iOS cannot be caught
 * when it goes: WebKit's limit is whichever is lower of its memory-pressure
 * limit and the system jetsam limit, it varies with device, load and uptime,
 * and the tab is killed without a JavaScript exception to handle. Budgeting
 * conservatively is the only available defence.
 */
export const LIMITED_DEVICE_MAX_BYTES_STREAMING = 1024 * 1024 * 1024
export const LIMITED_DEVICE_MAX_BYTES_BUFFERED = 200 * 1024 * 1024

/**
 * Past this many, the queue says out loud that encoding happens in this tab
 * and runs one file at a time. Not a limit, just an expectation.
 */
export const LONG_QUEUE_NOTICE_AT = 10

/**
 * A ceiling as a person would say it: "2 GB", "500 MB".
 *
 * Shared so the picker, the dialogs and the comparison table on the login
 * page all quote the same number from the same place. They used to carry
 * their own literals, which is how the landing page ended up advertising a
 * limit the code had not used for months.
 */
export function formatSizeLimit(bytes: number): string {
  const GIB = 1024 * 1024 * 1024
  if (bytes >= GIB) {
    const gib = bytes / GIB
    return (Number.isInteger(gib) ? String(gib) : gib.toFixed(1)) + " GB"
  }
  return Math.round(bytes / (1024 * 1024)) + " MB"
}

export const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024
export const HARD_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024

/**
 * Duration policy.
 *
 * Raised from the original 20-minute cap to 60 minutes. The browser-side
 * encoder has no inherent duration ceiling; the previous limit was a
 * conservative early-tool choice. Long videos at low bitrate (lectures,
 * field recordings) fit inside the size envelope without needing a
 * duration cap at all. The size boundaries above do the real
 * load-protection.
 */
export const MAX_DURATION_SECONDS = 60 * 60

/**
 * Whether an output deserves the high quality tier.
 *
 * This used to be decided in the worker by total pixel count, against a
 * 1280x720 threshold. Portrait video fails that arithmetic: 1080p portrait
 * is 608x1080, which is 656,640 pixels against the 921,600 the gate wanted,
 * so a 1080p phone recording was encoded at MEDIUM while a 720p landscape
 * clip got HIGH. Most phone video is portrait, so the common case was the
 * one being penalised. Found in a real 5 GB upload that came out at
 * 1.49 Mbps.
 *
 * The resolution the contributor picked already says what they want, and it
 * means the same thing in either orientation, so it is what decides now.
 * mediabunny's Quality values are subjective rather than absolute bitrates
 * and scale with the frame size, so this cannot over-spend on a small frame.
 */
export function wantsHighQuality(resolution: ResolutionChoice): boolean {
  return resolution === "original" || resolution === "1080p" || resolution === "720p"
}

export const MAX_TITLE_LENGTH = 240
export const MIN_TITLE_LENGTH = 3
export const SUGGESTED_DESCRIPTION_LENGTH = 500

export const TOOL_VERSION = "1.0.0"
export const TOOL_REPO_URL = "https://gitlab.wikimedia.org/toolforge-repos/clip2commons"
export const TOOL_LICENSE_URL = "https://gitlab.wikimedia.org/toolforge-repos/clip2commons/-/blob/main/LICENSE"
export const TOOL_MAINTAINER_NAME = "Dev Jadiya"
export const TOOL_MAINTAINER_URL = "https://meta.wikimedia.org/wiki/User:Dev_Jadiya"

/**
 * Bug-report / feedback link. Opens the maintainer's user-talk page with
 * the new-section editor pre-loaded with a "Clip2commons bug report"
 * heading. The user only has to write a description and hit "Save page".
 *
 * MediaWiki query params used:
 *   action=edit         -> open editor
 *   section=new         -> open the "add a new topic" form (heading box +
 *                          body box, separate from existing sections)
 *   preloadtitle=...    -> prefills the section heading
 *   summary=...         -> prefills the edit summary so the diff is
 *                          self-explanatory in the user's contribution log
 */
export const TOOL_FEEDBACK_URL =
  "https://meta.wikimedia.org/wiki/User_talk:Dev_Jadiya" +
  "?action=edit&section=new" +
  "&preloadtitle=" + encodeURIComponent("Clip2commons bug report") +
  "&summary=" + encodeURIComponent("Clip2commons bug report (via in-app feedback link)")

/** Optional MediaWiki change-tag to apply to uploads. Empty string = disabled.
 *  The tag must be registered on Commons via Special:Tags before it can be
 *  used; setting this before registration causes upload failures. */
export const UPLOAD_TAG = (import.meta.env.VITE_UPLOAD_TAG || "") as string

export const HAS_USER_OAUTH = !!(OAUTH_CLIENT_ID && OAUTH_REDIRECT_URI)

/**
 * Output file extension for a given media mode.
 *
 * Video output is WebM/VP9+Opus as always. Audio-only output is Opus in an
 * Ogg container: ".ogg" is on the Commons allowed-extension list, is what
 * mediabunny's OggOutputFormat declares for the container, and matches the
 * "application/ogg" + AUDIO mediatype Commons records for real Ogg audio
 * files - so extension, container and MIME all agree and MediaWiki's upload
 * verification has nothing to disagree with.
 *
 * Both the UI (title checks, filename preview) and the encoder derive the
 * output format from here, so the two can never drift apart.
 */
export function outputExtensionFor(mode: MediaMode | undefined): "webm" | "ogg" {
  return mode === "audio-only" ? "ogg" : "webm"
}

/**
 * Maximum length of a structured-data caption (a Wikibase MediaInfo label).
 *
 * Commons does not expose the real limit through the API. Wikibase's default
 * multilang string limit is 250 and UploadWizard enforces 255, so 250 sits
 * safely under both - and a caption is meant to be a one-line summary
 * anyway. The server stays the final authority: a caption it rejects is
 * reported as a warning and never fails an upload that already succeeded.
 */
export const MAX_CAPTION_LENGTH = 250
/* ----------------------------- Wikidata --------------------------------- */

/**
 * Wikidata's Action API.
 *
 * Reachable from this browser-only tool with no proxy, in two ways: item
 * search goes out anonymously with `origin=*`; the token fetch and the write
 * carry the OAuth bearer header plus `crossorigin`, exactly as the Commons
 * client does. The CSRF token does not carry over from Commons - tokens are
 * per-wiki. See wikidata-api.ts.
 */
export const WIKIDATA_API_URL = "https://www.wikidata.org/w/api.php"

/** Item page, for linking to what was just edited. */
export const WIKIDATA_ENTITY_BASE_URL = "https://www.wikidata.org/wiki/"

/**
 * The Wikidata properties this tool writes.
 *
 * Both are datatype `commonsMedia`, whose value is the bare page title with
 * no "File:" prefix. Which one applies depends on what was actually
 * uploaded: an audio-only upload publishes an Ogg audio file, and putting
 * that in P10 would assert it is a video. Confirmed against the live API -
 * P10 is labelled "video", P51 "audio".
 */
export const WIKIDATA_PROP_VIDEO = "P10"
export const WIKIDATA_PROP_AUDIO = "P51"
